import React from 'react';
import PropTypes from 'prop-types';
import autobind from 'class-autobind';
import classNames from 'classnames';
import styles from './styles.css';
import Link from 'components/Link';

// Utilities
import { clickOutside } from 'utils';


// Language
import { common as CommonLang } from 'language';


// Context
import { OverviewConsumer, OverviewContext } from 'pages/Overview/OverviewContext';


// Lodash
import { get, isObject } from 'lodash';


// Components
import Features from '../Features';
import Settings from '../Settings';
import Select from '../Select';
import ErrorBoundary from 'components/ErrorBoundary';


/**
 * Images
 */
/* eslint-disable global-require */
const Icons = {
	Arrow: require('../images/optimized/arrow--green.svg'),
	Features: require('../images/optimized/features.svg'),
	SelectArrow: require('../images/optimized/select-arrow.svg'),
	Settings: require('../images/optimized/settings.svg')
};
/* eslint-enable global-require */


export default class Controls extends React.PureComponent {
	constructor(props) {
		super(props);
		autobind(this);

		this.state = {
			activeMobileMenu: 'family' // This stores the active menu when in mobile mode. Needed only for dimming the family menu items when styles are covering.
		};

		this.menuFamily = React.createRef();
		this.menuStyle = React.createRef();
		this.panels = React.createRef();
		this.menus = React.createRef();
	}


	componentDidMount() {
		document.addEventListener('click', this.handleClickOutsideMenus, false);
		document.addEventListener('click', this.handleClickOutsidePanels, false);
	}


	componentWillUnmount() {
		document.removeEventListener('click', this.handleClickOutsideMenus, false);
		document.removeEventListener('click', this.handleClickOutsidePanels, false);
	}


	setActiveMobileMenu(type) {
		this.setState({
			activeMobileMenu: type
		});
	}


	settingsPanelRef(ref) {
		if (isObject(this)) {
			this.settingsPanel = ref;
		}
	}


	featuresPanelRef(ref) {
		if (isObject(this)) {
			this.featuresPanel = ref;
		}
	}


	/**
	 * Close any open menus when clicking outside of the menu.
	 *
	 * @method handleClickOutsideMenus
	 * @param  {Event}        event
	 * @return {Void}
	 */
	handleClickOutsideMenus(event) {
		const { activeMenu, putSetting } = this.context;

		const clickedOutside = clickOutside(event, [this.menus.current]);

		if (clickedOutside && activeMenu) {
			putSetting({ type: 'SET_ACTIVE_MENU', menu: null });
		}
	}


	/**
	 * Close any open menus when clicking outside of the menu.
	 *
	 * @method handleClickOutsidePanels
	 * @param  {Event}        event
	 * @return {Void}
	 */
	handleClickOutsidePanels(event) {
		const { activePanel, putSetting } = this.context;

		const eventTargetWhitelist = event.target.closest('#Editor');
		const clickedOutsideTabs = clickOutside(event, [this.panels.current, this.settingsPanel, this.featuresPanel, eventTargetWhitelist]);

		if (clickedOutsideTabs && activePanel) {
			putSetting({ type: 'SET_ACTIVE_PANEL', panel: null });
		}
	}


	/**
	 * Toggle menu’s open/close states
	 *
	 * @method toggleMenu
	 * @param  {Event}   event
	 * @return {Void}
	 */
	async toggleMenu(event) {
		const { activeMenu, putSetting } = this.context;

		const whichMenu = get(event, 'currentTarget.dataset.type') || null;
		const isActive = activeMenu === whichMenu ? null : whichMenu;

		await putSetting({ type: 'SET_ACTIVE_MENU', menu: isActive });
		await putSetting({ type: 'SET_ACTIVE_PANEL', panel: null });
	}


	async togglePanel(event) {
		const { putSetting } = this.context;

		const whichPanel = get(event, 'currentTarget.dataset.panel') || null;
		const activePanel = this.state.activePanel === whichPanel ? null : whichPanel;

		await putSetting({ type: 'SET_ACTIVE_PANEL', panel: activePanel });
	}


	/**
	 * Close all open menus
	 *
	 * @method closeMenu
	 * @return {Void}
	 */
	closeMenu() {
		const { putSetting } = this.context;

		putSetting({ type: 'SET_ACTIVE_MENU', menu: null });
		putSetting({ type: 'SET_ACTIVE_PANEL', panel: null });
	}


	buttonLabel(settings, type) {
		const labelPath = type === 'family' ? 'selectedFamily.label' : 'selectedStyle.label';
		const label = get(settings, labelPath, null);
		const language = type === 'family' ? 'Font family' : 'Font style';

		const selectedItemPath = type === 'family' ? 'selectedFamily' : 'selectedStyle';
		const isDimmed = !get(settings, selectedItemPath);

		return label || (
			<span
				className={ [
					styles.buttonLabel,
					isDimmed
						? styles['buttonLabel--isDimmed']
						: null
				].join(' ') }
			>
				{ language }
			</span>
		);
	}


	render() {
		const {
			breakpoints,
			className,
			productLine,
			products
		} = this.props;

		const { activeMobileMenu } = this.state;
		const { activeMenu, activePanel } = this.context;

		const hasRelatedPackage = get(this.context, 'settings.selectedStyle.relatedPackage.id', false);

		const ctaLink =
			hasRelatedPackage
				? `/fonts/${get(this.context, 'settings.selectedFamily.slug')}/styles/${get(this.context, 'settings.selectedStyle.relatedPackage.slug')}`
				: `/fonts/${get(productLine, 'attributes.slug')}/styles`;

		const familyMenuOptions = products;
		const styleMenuOptions = get(this.context.settings, 'selectedFamily.styles');

		return (
			<OverviewConsumer>
				{ ({ settings }) =>
					<div
						className={ classNames(className, styles.container) }
						role="presentation"
					>
						{/* Toolbar */}
						<div className={ styles.toolbar }>
							<ErrorBoundary>
								<Settings isOpen={ activePanel === 'settings' } settingsPanelRef={ this.settingsPanelRef } />
							</ErrorBoundary>
							<ErrorBoundary>
								<Features featuresPanelRef={ this.featuresPanelRef } isOpen={ activePanel === 'features' } />
							</ErrorBoundary>

							{ breakpoints.gtLargeMedium &&
								<div className={ styles.selections } ref={ this.menus }>
									<div
										className={ classNames({
											[styles.select]: activeMenu !== 'family',
											[styles['select--active']]: activeMenu === 'family'
										}) }
										ref={ this.menuFamily }
									>
										<Select
											breakpoints={ breakpoints }
											closeMenu={ this.closeMenu }
											collection={ familyMenuOptions }
											isActive={ activeMenu === 'family' }
											type="family"

										/>
										{ breakpoints.gtLargeMedium &&
											<button
												className={ styles.selectSwitch }
												data-type="family"
												onClick={ this.toggleMenu }
											>
												{ this.buttonLabel(settings, 'family') }
												<Icons.SelectArrow className={ styles['icon--selectArrow'] } />
											</button>
										}
									</div>

									<div
										className={ classNames({
											[styles.select]: activeMenu !== 'style',
											[styles['select--active']]: activeMenu === 'style'
										}) }
										ref={ this.menuStyle }
									>
										<Select
											breakpoints={ breakpoints }
											closeMenu={ this.closeMenu }
											collection={ styleMenuOptions }
											isActive={ activeMenu === 'style' }
											type="style"
										/>
										{ breakpoints.gtLargeMedium &&
											<button
												className={
													classNames(
														styles.selectSwitch,
														{ [styles['selectSwitch--isDisabled']]: !styleMenuOptions }
													)
												}
												data-type="style"
												onClick={ this.toggleMenu }
											>
												{ this.buttonLabel(settings, 'style') }
												<Icons.SelectArrow className={ styles['icon--selectArrow'] } />
											</button>
										}
									</div>
								</div>
							}

							{/* Select menus for mobile size. */}
							{ breakpoints.ltLargeMedium &&
								<div className={ classNames(styles.mobileFonts, { [styles['mobileFonts--active']]: activePanel === 'font' }) }>
									<Select
										activeMobileMenu={ activeMobileMenu }
										breakpoints={ breakpoints }
										closeMenu={ this.closeMenu }
										collection={ products }
										isActive={ activeMenu === 'family' }
										selectedID={ parseInt(productLine.id, 10) }
										type="family"
									/>
									<Select
										activeMobileMenu={ activeMobileMenu }
										breakpoints={ breakpoints }
										closeMenu={ this.closeMenu }
										collection={ get(settings, 'selectedFamily.styles') }
										isActive={ activeMenu === 'style' }
										setActiveMobileMenu={ this.setActiveMobileMenu }
										type="style"
									/>
								</div>
							}

							{ process.env.HAS_MOBILE_EDITOR || (!process.env.HAS_MOBILE_EDITOR && breakpoints.gtLargeMedium) &&
								<div className={ styles.tabs } ref={ this.panels }>
									<button
										className={ classNames(styles['tab--settings'], { [styles['tab--active']]: activePanel === 'settings' }) }
										data-panel="settings"
										onClick={ this.togglePanel }
									>
										<Icons.Settings className={ styles['icon--settings'] } />
									</button>
									<button
										className={ classNames(styles['tab--features'], { [styles['tab--active']]: activePanel === 'features' }) }
										data-panel="features"
										onClick={ this.togglePanel }
									>
										<Icons.Features />
									</button>
								</div>
							}

							<div className={ styles.cta }>
								<Link
									className={ styles.ctaLink }
									to={ ctaLink }
								>
									{ <Icons.Arrow className={ styles['icon--greenArrow'] } /> }
									{ (breakpoints.ltLargeMedium || breakpoints.gtLarge) && hasRelatedPackage && <em>Part of</em> }
									{ breakpoints.large && hasRelatedPackage && <em>In</em> }
									<strong>
										{ !hasRelatedPackage && CommonLang.buyFontsCTA }
										{ hasRelatedPackage && get(this.context, 'settings.selectedStyle.relatedPackage.name') }
									</strong>
								</Link>
							</div>
						</div>
					</div>
				}
			</OverviewConsumer>
		);
	}
}

Controls.contextType = OverviewContext;

Controls.propTypes = {
	breakpoints: PropTypes.object.isRequired,
	className: PropTypes.string,
	productLine: PropTypes.object,
	products: PropTypes.array
};

Controls.defaultProps = {
	className: null,
	productLine: null,
	products: null
};
